import React, { ReactElement, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import './StepperModal.scss';
import { useRecoilState } from 'recoil';
import { currentModalPageState } from '../../State';

interface StepperModalProps {
    modals: React.ReactNode[] | React.ReactElement[];
    onNextModal?: () => void;
    onPrevModal?: () => void;
    nextStepText?: string;
    previousStepText?: string;
    finishText: string;
    show: boolean;
    onHide: () => void;
    finishButtonOnClick: () => void;
    finishButtonVariant?: null | 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
}

const StepperModal: React.FC<StepperModalProps> = ({
    modals,
    nextStepText = 'Suivant',
    previousStepText = 'Précédent',
    finishText = 'Terminé',
    onNextModal,
    onPrevModal,
    show,
    onHide,
    finishButtonOnClick,
    finishButtonVariant = 'success',
}) => {
    const [showModal, setShowModal] = useState(show);
    const [currentModalPage, setCurrentModalPage] = useRecoilState(currentModalPageState);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        setShowModal(show);
    }, [show]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (windowWidth < 950) {
            setShowModal(false);
        } else if (show) {
            setShowModal(true);
        }
    }, [windowWidth, show]);

    const handleNextModal = () => {
        if (currentModalPage < modals.length - 1) {
            setCurrentModalPage(currentModalPage + 1);
            onNextModal && onNextModal();
        }
    };

    const handlePrevModal = () => {
        if (currentModalPage > 0) {
            setCurrentModalPage(currentModalPage - 1);
            onPrevModal && onPrevModal();
        }
    };

    const renderCircles = () => {
        return (modals as ReactElement[]).map((_, index) => (
            <div key={index} className={`circle ${currentModalPage === index ? 'active' : ''}`} />
        ));
    };

    return (
        <Modal dialogClassName="modal-stepper" show={showModal} onHide={onHide}>
            <Modal.Body className="custom-modal-body-size">
                <div className="stepper-modal-content" style={{ border: 'none' }}>
                    {modals[currentModalPage]}
                </div>
            </Modal.Body>
            <Modal.Footer style={{ border: 'none' }}>
                <div className="stepper-modal-footer" style={{ border: 'none' }}>
                    <div className="image-container">
                        <img
                            className="logo"
                            src="https://images-jp-prod.nyc3.cdn.digitaloceanspaces.com/Images/Misc/logo192.png"
                            alt="Image"
                        />
                    </div>
                    <div className="circles-container">{modals.length > 1 && renderCircles()}</div>
                    <div className="button-container">
                        {currentModalPage > 0 ? (
                            <Button size="lg" className="previous-button" onClick={handlePrevModal}>
                                {previousStepText}
                            </Button>
                        ) : (
                            <Button size="lg" className="previous-button" onClick={handlePrevModal} disabled>
                                {previousStepText}
                            </Button>
                        )}
                        {currentModalPage < modals.length - 1 ? (
                            <Button size="lg" className="next-button" onClick={handleNextModal}>
                                {nextStepText}
                            </Button>
                        ) : (
                            <Button
                                size="lg"
                                className="finish-button"
                                variant={finishButtonVariant as string}
                                onClick={finishButtonOnClick}
                            >
                                {finishText}
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default StepperModal;
